/**
 * Nova peça — o "multi comando".
 *
 * Uma arte, várias contas, horários independentes. O `POST /posts` cria tudo
 * numa transação e valida POR DESTINO com o adapter daquela plataforma — então
 * a resposta consegue dizer "subiu no Instagram das quatro, mas o formato
 * STORY não existe no LinkedIn do AntiFila" antes de qualquer agendamento
 * existir. Esta tela só precisa mostrar essa resposta bem.
 *
 * A peça nasce em `draft`, e o worker NUNCA pega `draft`. Enfileirar é um
 * segundo ato, deliberado.
 */
import { Api, ApiError, type Conta, type MidiaGuardada } from "../api.js";
import { el, limpar } from "../dom.js";
import { marcaAtual } from "../marca.js";
import { receberPeca } from "../passagem.js";
import { avisar, type Tela } from "../tela.js";

/**
 * Os formatos oferecidos.
 *
 * Fixos aqui porque a API não expõe o `supports` do adapter. A consequência é
 * conhecida: oferecer um formato que a conta não aceita produz recusa na
 * criação — com a mensagem certa, vinda do adapter, e ANTES de existir
 * agendamento. Não é silencioso, só é tarde demais em um passo.
 */
const FORMATOS = ["IMAGE", "CAROUSEL", "REELS", "STORY"] as const;

/** Teto de legenda do Instagram. */
const MAX_LEGENDA = 2200;

/**
 * `datetime-local` → ISO com fuso.
 *
 * O valor de um `datetime-local` é "2026-09-15T09:00", **sem fuso nenhum** — e
 * a API recusa horário sem fuso de propósito, porque "nove horas" não diz se é
 * em São Paulo ou em Londres e adivinhar erra por três.
 *
 * `new Date(valor)` interpreta no fuso do navegador, que é o de quem está
 * agendando, e `toISOString()` devolve UTC com `Z`. Passa a ser inequívoco sem
 * a gente montar string de offset à mão — que é onde esse tipo de código
 * costuma errar por uma hora no horário de verão.
 */
export function comFuso(valorLocal: string): string {
  const d = new Date(valorLocal);
  if (Number.isNaN(d.getTime())) {
    throw new Error(`Horário inválido: "${valorLocal}".`);
  }
  return d.toISOString();
}

/**
 * ISO com fuso → o formato que o `datetime-local` aceita.
 *
 * O caminho de volta do `comFuso`: o input não entende fuso, então recebe a
 * hora já no relógio local de quem está olhando.
 *
 * Montado com `getFullYear` e companhia, e NÃO com `toISOString().slice(0,16)`
 * — o ISO é UTC, e cortar a string preencheria o campo com três horas de
 * diferença. É o mesmo erro que a invariante 1 do projeto existe para evitar,
 * só que na direção contrária.
 */
export function paraCampoLocal(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const p = (n: number): string => String(n).padStart(2, "0");
  return (
    `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}` +
    `T${p(d.getHours())}:${p(d.getMinutes())}`
  );
}

/** Daqui a uma hora, arredondado, no formato do input. */
function sugestaoDeHorario(): string {
  const d = new Date(Date.now() + 60 * 60 * 1000);
  d.setMinutes(0, 0, 0);
  const p = (n: number): string => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
}

export function telaNovaPeca(): Tela {
  return {
    titulo: "Nova peça",
    async montar(raiz) {
      // Só as contas e a mídia DA MARCA escolhida no topo. A API recusa de
      // qualquer jeito mídia de uma marca na conta de outra; aqui a opção
      // errada simplesmente não aparece.
      const marca = marcaAtual();
      const [{ accounts }, { media }] = await Promise.all([
        Api.contas(marca.slug),
        Api.midias(marca.slug)
      ]);
      const pre = receberPeca(marca.slug);

      const escolhidas: number[] = [];
      const horarios = new Map<number, HTMLInputElement>();

      // ---------------------------------------------------------- formato
      const formato = el(
        "select",
        {},
        ...FORMATOS.map((f) => el("option", { value: f }, f))
      ) as HTMLSelectElement;

      // ---------------------------------------------------------- legenda
      const legenda = el("textarea", {
        rows: "4",
        placeholder: "legenda…"
      }) as HTMLTextAreaElement;
      const contador = el("span", { class: "sub" }, `0 / ${MAX_LEGENDA}`);
      legenda.addEventListener("input", () => {
        const n = legenda.value.length;
        contador.textContent = `${n} / ${MAX_LEGENDA}`;
        contador.className = n > MAX_LEGENDA ? "erro-linha" : "sub";
      });

      const avisoStory = el("p", { class: "sub", hidden: true });
      formato.addEventListener("change", () => {
        const story = formato.value === "STORY";
        avisoStory.hidden = !story;
        avisoStory.textContent = story
          ? "Story não aceita legenda — o texto será recusado na criação."
          : "";
      });

      // ------------------------------------------------------------ mídia
      const galeria = el("div", { class: "escolher-midia" });
      for (const m of media)
        galeria.appendChild(cartaoMidia(m, escolhidas, galeria));
      if (media.length === 0) {
        galeria.appendChild(
          el(
            "p",
            { class: "sub" },
            `Nenhuma mídia de ${marca.nome}. Suba na aba Mídia ou gere em Artes.`
          )
        );
      }

      // Vinda do editor de carrossel: formato e ordem já decididos lá.
      const avisoPre = el("p", { class: "aviso ok", hidden: true });
      if (pre) {
        formato.value = pre.format;
        escolhidas.push(
          ...pre.mediaIds.filter((id) => media.some((m) => m.mediaId === id))
        );
        pintarEscolhidas(galeria, escolhidas);
        avisoPre.hidden = false;
        avisoPre.textContent =
          `${escolhidas.length} artes do editor, já na ordem do carrossel — a capa é a 1. ` +
          `Confira a sequência antes de criar.`;
      }

      // ----------------------------------------------------------- contas
      const listaContas = el("div", { class: "escolher-contas" });
      for (const c of accounts) {
        listaContas.appendChild(linhaConta(c, horarios));
      }

      // ---------------------------------------------------------- enviar
      const resultado = el("div");
      const criar = el(
        "button",
        {},
        "Criar como rascunho"
      ) as HTMLButtonElement;

      criar.addEventListener("click", () => {
        limpar(resultado);
        criar.disabled = true;

        void (async () => {
          try {
            const targets = [...horarios.entries()]
              .filter(([, input]) => !input.disabled && input.value !== "")
              .map(([accountId, input]) => ({
                accountId,
                scheduledAt: comFuso(input.value)
              }));

            if (targets.length === 0) {
              throw new Error(
                "Escolha ao menos uma conta e um horário. Sem destino, não há o que agendar."
              );
            }

            const peca = await Api.criarPeca({
              format: formato.value,
              body: legenda.value.trim() === "" ? null : legenda.value,
              mediaIds: [...escolhidas],
              targets
            });

            resultado.appendChild(
              depoisDeCriar(peca.postId, targets.length, resultado)
            );
          } catch (err) {
            // A validação por destino devolve `detail` com conta e motivo.
            // Mostrar isso é o ponto inteiro do multi comando.
            if (err instanceof ApiError && Array.isArray(err.detail)) {
              resultado.appendChild(detalhesPorDestino(err));
            } else {
              avisar(resultado, err, "Não deu para criar a peça");
            }
          } finally {
            criar.disabled = false;
          }
        })();
      });

      raiz.appendChild(
        el(
          "div",
          { class: "formulario" },
          campo("Formato", formato, avisoStory),
          campo("Legenda", legenda, contador),
          avisoPre,
          campo("Mídia", galeria),
          campo(`Contas de ${marca.nome} e horários`, listaContas),
          criar,
          resultado
        )
      );

      // Um horário sugerido já preenchido evita o erro mais comum: agendar
      // para um instante no passado, que o worker publica no ciclo seguinte.
      const sugestao = sugestaoDeHorario();
      for (const input of horarios.values()) input.value = sugestao;
    }
  };
}

function campo(rotulo: string, ...conteudo: HTMLElement[]): HTMLElement {
  return el("div", { class: "campo" }, el("label", {}, rotulo), ...conteudo);
}

function cartaoMidia(
  m: MidiaGuardada,
  escolhidas: number[],
  galeria: HTMLElement
): HTMLElement {
  const card = el(
    "button",
    { class: "opcao-midia", type: "button" },
    m.kind === "video"
      ? el("video", { src: m.publicUrl, preload: "metadata", muted: true })
      : el("img", { src: m.publicUrl, alt: "", loading: "lazy" }),
    el("span", { class: "ordem" })
  );

  card.addEventListener("click", () => {
    const i = escolhidas.indexOf(m.mediaId);
    if (i >= 0) escolhidas.splice(i, 1);
    else escolhidas.push(m.mediaId);

    pintarEscolhidas(galeria, escolhidas);
  });

  card.dataset["id"] = String(m.mediaId);
  return card;
}

/**
 * Marca cada miniatura escolhida com a sua posição.
 *
 * A ORDEM importa no carrossel: o primeiro item define a proporção que o
 * Instagram aplica aos demais. Mostrar o número escolhido evita a surpresa de
 * descobrir a ordem só depois de publicado.
 */
function pintarEscolhidas(galeria: HTMLElement, escolhidas: number[]): void {
  for (const botao of galeria.querySelectorAll<HTMLElement>(".opcao-midia")) {
    const id = Number(botao.dataset["id"]);
    const pos = escolhidas.indexOf(id);
    botao.classList.toggle("marcada", pos >= 0);
    const ordem = botao.querySelector(".ordem");
    if (ordem) ordem.textContent = pos >= 0 ? String(pos + 1) : "";
  }
}

function linhaConta(
  c: Conta,
  horarios: Map<number, HTMLInputElement>
): HTMLElement {
  const quando = el("input", { type: "datetime-local" }) as HTMLInputElement;
  quando.disabled = true;

  const marca = el("input", { type: "checkbox" }) as HTMLInputElement;
  marca.addEventListener("change", () => {
    quando.disabled = !marca.checked;
  });

  horarios.set(c.id, quando);

  return el(
    "div",
    { class: "linha-conta" },
    el("label", {}, marca, ` @${c.handle}`),
    quando,
    // Conta desligada aceita agendamento e NÃO publica — o worker a ignora de
    // propósito. Sem este aviso, o sintoma seria o alarme
    // `inactive_account_with_schedule` dias depois.
    c.isActive
      ? null
      : el("span", { class: "erro-linha" }, "desligada — não vai publicar")
  );
}

function detalhesPorDestino(err: ApiError): HTMLElement {
  const box = el(
    "div",
    { class: "aviso erro" },
    el("strong", {}, "Nenhum destino foi criado"),
    el(
      "p",
      {},
      "A validação roda por destino e ANTES de criar qualquer coisa — " +
        "criar metade deixaria a peça num estado que ninguém sabe descrever."
    )
  );

  for (const d of err.detail as {
    handle?: string;
    errors?: { message?: string }[];
  }[]) {
    box.appendChild(
      el(
        "div",
        { class: "por-destino" },
        el("strong", {}, `@${d.handle ?? "?"}`),
        ...(d.errors ?? []).map((e) => el("p", {}, e.message ?? ""))
      )
    );
  }
  return box;
}

function depoisDeCriar(
  postId: number,
  quantos: number,
  raiz: HTMLElement
): HTMLElement {
  const enfileirar = el(
    "button",
    {},
    "Aprovar e enfileirar"
  ) as HTMLButtonElement;

  enfileirar.addEventListener("click", () => {
    if (
      !confirm(
        `Enfileirar ${quantos} destino(s)?\n\n` +
          `A partir daqui o worker publica no horário agendado, no perfil real. ` +
          `Post publicado não se edita nem se remove por API.`
      )
    )
      return;

    enfileirar.disabled = true;
    void Api.enfileirar(postId)
      .then((r) => {
        limpar(raiz);
        raiz.appendChild(
          el(
            "div",
            { class: "aviso ok" },
            el("strong", {}, `${r.queued} destino(s) na fila`),
            el(
              "p",
              {},
              "O worker publica no horário. Acompanhe pelo Calendário."
            ),
            el("a", { href: "#/calendario" }, "Ver o calendário")
          )
        );
      })
      .catch((e: unknown) => {
        enfileirar.disabled = false;
        avisar(raiz, e, "Não deu para enfileirar");
      });
  });

  return el(
    "div",
    { class: "aviso ok" },
    el("strong", {}, `Peça ${postId} criada como rascunho`),
    el(
      "p",
      {},
      `${quantos} destino(s) em draft. O worker não pega rascunho — nada será ` +
        `publicado enquanto você não aprovar.`
    ),
    enfileirar
  );
}
